/**
 * The plant's action list (the 2026-10-06 next-phase plan, step 2; platform-core twin/actions_list.go):
 *
 *   GET /v1/twin/actions   every recommendation the briefs and the published studies raise, and every one the plant
 *                          recorded an event against, once per key: its words, the defects it is raised for and where
 *                          it was raised; each key's state and the plant's verdict beside the items, as a brief serves
 *                          them beside its document.
 *
 * Gated as the briefs are: the foundry pack (403 pack_not_enabled without it), no vendor overlay (403 vendor_no_twin).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { errorCodeOf, isProductRefused } from "./error";
import { useBriefLive, type BriefWatch, type Savings } from "./brief";
import type { RecommendationAssignments, RecommendationStates, RecommendationVerdicts } from "./recommendations";

/** A period's brief a recommendation stands in: its key and its first plant day ("" when its brief names none). */
export interface ActionPeriod {
  period_key: string;
  first_day: string;
}

/** A published study a recommendation stands in. */
export interface ActionStudy {
  id: string;
  title: string;
  published_at: string;
}

/** Where a recommendation is raised, each list newest first; all empty for one only the history holds. */
export interface ActionRaised {
  overall: boolean;
  weeks: ActionPeriod[];
  months: ActionPeriod[];
  studies: ActionStudy[];
}

/** A defect a recommendation is raised for, with the label a brief gives it (its id when none does). */
export interface ActionDefect {
  defect: string;
  label: string;
}

/**
 * An open casting hold on the list (station workspaces, step 6): its own state, never a recommendation's. `heats` and
 * `disposed` count the heats it names and those disposed of; `can_act` says whether the caller may act on it.
 */
export interface ActionHold {
  id: string;
  state: "suggested" | "confirmed";
  label: string;
  entity_id: string;
  day: string;
  heats: number;
  disposed: number;
  can_act: boolean;
}

/**
 * One item on the list; kind is its key's (alarm_rule, report, playbook, range, ask, study, intervention), or
 * casting_hold for an open casting hold, which carries `hold` and is never marked as a recommendation is.
 */
export interface ActionItem {
  key: string;
  kind: string;
  text: string;
  defects: ActionDefect[];
  raised: ActionRaised;
  hold?: ActionHold;
  /** The threshold of the overall brief's watch the text is (brief/v19), in the org's display units as served. */
  watch?: BriefWatch;
}

/** What the list was read from: unread counts the briefs and studies left out because they did not read. */
export interface ActionsRead {
  overall: boolean;
  weeks: number;
  months: number;
  studies: number;
  unread: number;
  history_capped: boolean;
  /** The open casting holds did not read, and the list is served without them (step 6). */
  casting_holds_unread?: boolean;
  /** The owners and due dates did not read, and the list is served without them. */
  assignments_unread?: boolean;
}

export interface ActionsList {
  items: ActionItem[];
  recommendation_states: RecommendationStates;
  recommendation_verdicts: RecommendationVerdicts;
  read: ActionsRead;
  /** What the plant's improved changes saved, in money, as the overall brief serves them; absent for none. */
  savings?: Savings;
  /** Each listed recommendation's owner and due date (migration 0250); absent from an older API. */
  recommendation_assignments?: RecommendationAssignments;
  /** The overall brief's money by defect (B2): absent with no overall brief, or one composed before brief/v16. */
  money?: ActionsMoney;
}

/** The overall brief's Pareto by defect: what it is ranked by, its currency, and each defect's pieces and cost. */
export interface ActionsMoney {
  ranked_by: "money" | "pieces";
  currency?: string;
  /** `priced`: the pieces the cost could price, below `pieces` where the cost is partial. */
  by_defect: Record<string, { pieces: number; cost?: number; priced?: number }>;
}

export const ACTIONS_KEY = ["twin-actions"] as const;

export const actionsApi = {
  list: () => api.get<ActionsList>("/v1/twin/actions"),
};

/** Why the list is not this caller's to read, where a retry changes nothing: the org has no foundry pack, the caller
 * is a vendor overlay on the org (the briefs' own gates), or the org's Foundry Twin is not active (the product gate).
 * Null for any other failure. */
export function gatedReason(err: unknown): "pack" | "vendor" | "product" | null {
  const code = errorCodeOf(err);
  if (code === "pack_not_enabled") return "pack";
  if (code === "vendor_no_twin") return "vendor";
  if (isProductRefused(err)) return "product";
  return null;
}

/** The org's action list. A recording anywhere reads it again (useRecordRecommendationEvent). */
export function useActionsQuery(enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: ACTIONS_KEY,
    queryFn: actionsApi.list,
    enabled: live && enabled,
    staleTime: 30_000,
    retry: (failures, err) => gatedReason(err) === null && failures < 2,
  });
}
